
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

import timeAgo from '../utils/timeAgo';
import HomeFeaturedListings from '../components/homefeaturedlistings';
import { Link } from 'react-router-dom';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const Home = () => {
  const [showToast, setShowToast] = useState(false);
  const [articles, setArticles] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('Coastal');
  const [modalArticle, setModalArticle] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const decodeEncodedHtml = (value) => {
    const input = value || '';
    if (!input.includes('&lt;') && !input.includes('&gt;') && !input.includes('&amp;')) {
      return input;
    }

    let decoded = input;
    const textarea = document.createElement('textarea');
    for (let i = 0; i < 3; i += 1) {
      textarea.innerHTML = decoded;
      const next = textarea.value;
      if (next === decoded) break;
      decoded = next;
    }
    return decoded;
  };

  const getSanitizedDescription = (html) => DOMPurify.sanitize(decodeEncodedHtml(html), {
    USE_PROFILES: { html: true },
    FORCE_BODY: true,
    ADD_ATTR: ['style', 'color', 'face', 'size'],
  });

  // Format a date like '4th Dec 2025'
  const formatDateWithOrdinal = (input) => {
    if (!input) return '';
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return '';
    const day = d.getDate();
    const getOrdinal = (n) => {
      const s = ["th","st","nd","rd"], v = n%100;
      return s[(v-20)%10] || s[v] || s[0];
    };
    const month = d.toLocaleString(undefined, { month: 'short' });
    const year = d.getFullYear();
    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };
  
  


  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2000);
        })
        .catch(err => {
          console.error('Clipboard copy failed:', err);
          fallbackCopyToClipboard(text);
        });
    } else {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Copy to clipboard failed. Link: ' + text);
    }
  };



  useEffect(() => {
    if (selectedMenu === 'Coastal') {
      // Try compact summary first, then fall back to full news if empty — add console logs to aid debugging on mobile
      fetch(`/api/news/summary?section=Coastal&limit=12`)
        .then(res => res.json())
            .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            const mapped = data.map(item => ({
              nid: item.nid,
              title: item.title,
              // summary endpoint returns 'snippet' (short text) rather than full aboutdescription
              aboutdescription: item.snippet || item.aboutdescription || '',
              imagepath: item.imagepath || null,
              validitystartdate: item.validity_startdate || null,
              locationname: item.locationname || item.newssection || ''
            }));
            setArticles(mapped);
          } else {
            // fallback to full news endpoint when summary returns empty
            console.warn('Summary returned no items, falling back to full /api/news');
            fetch(`/api/news?section=Coastal`)
              .then(r => r.json())
              .then(full => {
                  if (Array.isArray(full) && full.length > 0) {
                  const mappedFull = full.map(item => ({
                    nid: item.nid,
                    title: item.title,
                    aboutdescription: item.aboutdescription || '',
                    imagepath: item.imagepath || (item.photos && item.photos[0]) || null,
                    validitystartdate: item.validity_startdate || item.createddate || null,
                    photos: item.photos || [],
                    locationname: item.locationname || item.newssection || ''
                  }));
                  setArticles(mappedFull);
                } else {
                  setArticles([]);
                }
              })
              .catch(err => {
                console.error('Failed to fetch full news fallback:', err);
                setArticles([]);
              });
          }
        })
        .catch(err => {
          console.error('Failed to fetch news summary:', err);
          setArticles([]);
        });
    } else if (selectedMenu === 'Gulf') {
      fetch(`/api/news/summary?section=Gulf&limit=12`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            const mapped = data.map(item => ({
              nid: item.nid,
              title: item.title,
              description: item.snippet || item.aboutdescription || '',
              imagepath: item.imagepath || null,
              validitystartdate: item.validity_startdate || null,
              locationname: item.locationname || item.newssection || ''
            }));
            setArticles(mapped);
          } else {
            console.warn('Gulf news summary returned no items, falling back to full /api/news');
            fetch(`/api/news?section=Gulf`)
              .then(r => r.json())
              .then(full => {
                if (Array.isArray(full) && full.length > 0) {
                  const mappedFull = full.map(item => ({
                    nid: item.nid,
                    title: item.title,
                    aboutdescription: item.aboutdescription || '',
                    imagepath: item.imagepath || (item.photos && item.photos[0]) || null,
                    validitystartdate: item.validity_startdate || item.createddate || null,
                    photos: item.photos || [],
                    locationname: item.locationname || item.newssection || ''
                  }));
                  setArticles(mappedFull);
                } else {
                  setArticles([]);
                }
              })
              .catch(err => {
                console.error('Failed to fetch full Gulf news fallback:', err);
                setArticles([]);
              });
          }
        })
        .catch(err => {
          console.error('Failed to fetch Gulf news summary:', err);
          setArticles([]);
        });
    } else if (selectedMenu === 'International') {
      fetch(`/api/news/summary?section=International&limit=12`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            const mapped = data.map(item => ({
              nid: item.nid,
              title: item.title,
              aboutdescription: item.aboutdescription || item.aboutdescription || '',
              imagepath: item.imagepath || null,
              validitystartdate: item.validity_startdate || null,
              locationname: item.locationname || item.newssection || ''
            }));
            setArticles(mapped);
          } else {
            console.warn('International news summary returned no items, falling back to full /api/news');
            fetch(`/api/news?section=International`)
              .then(r => r.json())
              .then(full => {
                if (Array.isArray(full) && full.length > 0) {
                  const mappedFull = full.map(item => ({
                    nid: item.nid,
                    title: item.title,
                    aboutdescription: item.aboutdescription || '',
                    imagepath: item.imagepath || (item.photos && item.photos[0]) || null,
                    validitystartdate: item.validity_startdate || item.createddate || null,
                    photos: item.photos || [],
                    locationname: item.locationname || item.section || ''
                  }));
                  setArticles(mappedFull);
                } else {
                  setArticles([]);
                }
              })
              .catch(err => {
                console.error('Failed to fetch full International news fallback:', err);
                setArticles([]);
              });
          }
        })
        .catch(err => {
          console.error('Failed to fetch International news summary:', err);
          setArticles([]);
        });
    } else if (selectedMenu === 'Special') {
      // Get data from specials.jsx instead of local data
      setArticles([]); // Clear local articles, specials handled in specials.jsx
    } else {
      setArticles([]);
    }
    setModalArticle(null);
  }, [selectedMenu]);

  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      <header style={{ background: '#e8edf3ff', color: '#37864fff', padding: '0.25rem', textAlign: 'center', borderRadius: 8, marginBottom: 2 }}>
        <h1 style={{ margin: 0, marginTop: 0, fontSize: '1.0rem', fontWeight: 700 }}>News & Updates</h1>
      </header>

      {/* Featured Listing Section */}
      <div style={{ marginBottom: '-1.4rem', marginTop: '0.25rem' }}>
        {/* Import featuredlistingsdata and filter/sort here */}
        {(() => {
          // Dynamically import featuredlistingsdata
          const featuredlistingsdata = require('../data/homefeatureddata').default;
          // Filter by accountstatus 'Active'
          const activeListings = featuredlistingsdata.filter(l => l.accountstatus === 'Active');
          // Sort by listingorder ascending
          const sortedListings = activeListings.sort((a, b) => (a.listingorder || 9999) - (b.listingorder || 9999));
          return <HomeFeaturedListings featuredListings={sortedListings} />;
        })()}
      </div>

      {/* Tab Section for News */}
      <div style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: '0.35rem', fontWeight: 500, marginBottom: 0 }}>
          {['Coastal', 'Gulf', 'International', 'Special'].map((menu) => (
            menu === 'Special' ? (
              <Link
                key={menu}
                to="/specials"
                style={{
                  padding: '0.48rem 1rem',
                  fontSize: '0.82rem',
                  borderRadius: 8,
                  border: selectedMenu === menu ? '2px solid #1976d2' : '1px solid #ccc',
                  background: selectedMenu === menu ? '#e3f2fd' : '#fff',
                  color: selectedMenu === menu ? '#1976d2' : '#333',
                  fontWeight: selectedMenu === menu ? 600 : 400,
                  cursor: 'pointer',
                  outline: 'none',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
                onClick={() => setSelectedMenu(menu)}
              >
                {menu}
              </Link>
            ) : (
              <button
                key={menu}
                onClick={() => setSelectedMenu(menu)}
                style={{
                  padding: '0.48rem 1rem',
                  fontSize: '0.82rem',
                  borderRadius: 8,
                  border: selectedMenu === menu ? '2px solid #1976d2' : '1px solid #ccc',
                  background: selectedMenu === menu ? '#e3f2fd' : '#fff',
                  color: selectedMenu === menu ? '#1976d2' : '#333',
                  fontWeight: selectedMenu === menu ? 600 : 400,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {menu}
              </button>
            )
          ))}
        </div>
        {/* News List */}
        {articles.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0.3rem 0 0 0' }}>
            {articles.map((article, idx) => (
              <li
                key={idx}
                style={{
                  padding: '0.7rem',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  cursor: !article.sharelink ? 'pointer' : 'default',
                }}
                onClick={() => {
                  // Interview modals now handled via specials.jsx
                  // For regular articles (no aid or external link), show the generic modal only if no sharelink
                  if (!article.sharelink) {
                    // If we have an nid for the article, fetch the full article so aboutdescription is available
                    if (article && article.nid) {
                      console.log(`[Home Modal] Clicking article with nid=${article.nid}, title="${article.title}"`);
                      setModalLoading(true);
                      fetch(`/api/news/${article.nid}`)
                        .then(r => {
                          if (!r.ok) throw new Error('Failed to fetch full article');
                          return r.json();
                        })
                        .then(full => {
                          console.log(`[Home Modal] Fetched full article:`, full);
                          setModalArticle(full);
                          setModalLoading(false);
                        })
                        .catch(err => {
                          console.error('Failed to load full article, falling back to summary:', err);
                          setModalArticle(article);
                          setModalLoading(false);
                        });
                    } else {
                      setModalArticle(article);
                    }
                  }
                  // If there's a sharelink, don't open any modal (the link is clickable in the JSX)
                }}
              >
                {/* Image / Placeholder */}
                <div
                  style={{
                    width: 100,
                    height: 98,
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: '#ddd',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={article.imagepath ? article.imagepath : placeholderImg}
                    alt={article.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholderImg;
                    }}
                  />
                </div>
                {/* Text Content */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.92rem', marginBottom: '0.4rem' }}>
                    {article.sharelink ? (
                      <a
                        href={article.sharelink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                        onClick={e => e.stopPropagation()}
                      >
                        {article.title}
                      </a>
                    ) : (
                      <span style={{ color: '#1976d2' }}>{article.title}</span>
                    )}
                  </div>
                  {/* Relative time or postedtime */}
                  {article.validitystartdate && (
                    <div style={{ color: '#757575', fontSize: '11px', marginBottom: 5 }}>{timeAgo(article.validitystartdate)}</div>
                  )}
                  {article.validitystartdate && !article.validitystartdate && (
                    <div style={{ color: '#757575', fontSize: '11px', marginBottom: 5 }}>{article.postedtime}</div>
                  )}
                  <div style={{ 
                    color: '#333', 
                    fontSize: '0.82rem',
                    lineHeight: 1.6,
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-line',
                    overflowWrap: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  dangerouslySetInnerHTML={{ __html: getSanitizedDescription(article.aboutdescription || '') }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    {/* Modal for Coastal News Details */}
    {modalArticle && (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }} onClick={() => setModalArticle(null)}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 20, minWidth: 320, maxWidth: 450, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 2px 16px rgba(0,0,0,0.15)', position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button onClick={() => setModalArticle(null)} style={{ position: 'absolute', top: 8, right: 12, background: '#1976d2', border: 'none', color: '#fff', fontSize: 22, padding: '6px 12px', borderRadius: 14, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>&times;</button>
          {modalLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', fontSize: '0.9rem', color: '#666' }}>
              Loading article details...
            </div>
          )}
          {!modalLoading && (
            <>
          <div style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 8, marginBottom: 12, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={modalArticle.imagepath ? modalArticle.imagepath : placeholderImg} alt={modalArticle.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
          </div>
          <div style={{ fontWeight: 700, color: '#1976d2', fontSize: '1.12rem', marginBottom: 0 }}>{modalArticle.title}</div>
          <div style={{ color: '#333', fontSize: '0.82rem', marginBottom: 8, lineHeight: 1.8, wordWrap: 'break-word', whiteSpace: 'pre-line', overflowWrap: 'break-word' }}>
            {(() => {
              // Prefer aboutdescription, then description, then snippet
              const raw = (modalArticle && (modalArticle.aboutdescription || modalArticle.aboutdescription || modalArticle.snippet || '')) || '';
              const sanitizedDescription = getSanitizedDescription(raw);
              const metaParts = [];
              if (modalArticle && modalArticle.locationname) metaParts.push(modalArticle.locationname);
              if (modalArticle && (modalArticle.validity_startdate || modalArticle.date || modalArticle.createddate)) {
                const dateVal = modalArticle.validity_startdate || modalArticle.date || modalArticle.createddate;
                const formatted = formatDateWithOrdinal(dateVal);
                if (formatted) metaParts.push(formatted);
              }
              const metaString = metaParts.length ? metaParts.join(' ') + ' : ' : '';
              // Render metadata and then the normalized description (preserve newlines)
              return (
                <div>
                  <span style={{ color: '#666', fontWeight: 500, fontSize: '0.80rem' }}>{metaString}</span>
                  <div dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                </div>
              );
            })()}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
            {/* Share Button with Dropdown */}
            <button
              aria-label="Share this news article"
              onClick={(e) => {
                e.stopPropagation();
                setShowShareMenu(!showShareMenu);
              }}
              style={{
                padding: '0.5rem 1rem',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 500,
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                position: 'relative',
                zIndex: 10
              }}
            >
              Share
            </button>

            {/* Share Menu Dropdown */}
            {showShareMenu && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: '0.5rem',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 11,
                  minWidth: '150px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* WhatsApp Option */}
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(modalArticle.title + ' ' + (modalArticle.sharelink || window.location.href))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShareMenu(false);
                  }}
                  style={{
                    display: 'block',
                    padding: '0.75rem 1rem',
                    color: '#fff',
                    backgroundColor: '#25D366',
                    textDecoration: 'none',
                    borderBottom: '1px solid #eee',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#20BA58')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#25D366')}
                >
                  📱 WhatsApp
                </a>

                {/* Email Option */}
                <a
                  href={`mailto:?subject=${encodeURIComponent(modalArticle.title)}&body=${encodeURIComponent(modalArticle.description + '\n\nRead more: ' + (modalArticle.sharelink || window.location.href))}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShareMenu(false);
                  }}
                  style={{
                    display: 'block',
                    padding: '0.75rem 1rem',
                    color: '#333',
                    textDecoration: 'none',
                    borderBottom: '1px solid #eee',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#f5f5f5')}
                  onMouseLeave={(e) => (e.target.style.background = '#fff')}
                >
                  ✉️ Email
                </a>

                {/* Copy Link Option */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(modalArticle.sharelink || window.location.href);
                    setShowShareMenu(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#fff',
                    color: '#333',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#f5f5f5')}
                  onMouseLeave={(e) => (e.target.style.background = '#fff')}
                >
                  🔗 Copy Link
                </button>
              </div>
            )}

            {modalArticle.sharelink && (
              <a
                aria-label="View full article in new tab"
                href={modalArticle.sharelink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#f5f5f5',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                View Full Article
              </a>
            )}
          </div>
          {showToast && (
            <div style={{
              position: 'fixed',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#333',
              color: '#fff',
              padding: '0.75rem 1.5rem',
              borderRadius: 8,
              fontSize: '1rem',
              zIndex: 10000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
              Link copied to clipboard!
            </div>
          )}
            </>
          )}
        </div>
      </div>
    )}
  </div>
  );
}

export default Home;
